import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import VerwendungenModal, { DrilldownButton } from '../components/VerwendungenModal';

// ── Formatter (lokal, kleine Helfer; analog zu Projektpositionen.js) ────────
function fmtNum(n) {
  if (n == null || n === '') return '–';
  const v = parseFloat(n);
  if (isNaN(v)) return '–';
  return v.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}
function fmtDatum(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE');
}
function fmtEur(n) {
  if (n == null || n === '') return '–';
  const v = parseFloat(n);
  if (isNaN(v)) return '–';
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtProz(p) {
  if (p == null) return '—';
  return p.toFixed(p >= 10 ? 0 : 1) + ' %';
}
// Stunden als „12h 30m" — gleiche Optik wie BaustellenAuswertung,
// damit der Chef nicht zwischen Notationen springen muss.
function fmtStunden(h) {
  if (h == null || h === '') return '0h';
  const v = parseFloat(h);
  if (isNaN(v) || v === 0) return '0h';
  const hh = Math.floor(v);
  const mm = Math.round((v - hh) * 60);
  return `${hh}h ${mm.toString().padStart(2, '0')}m`;
}

// Schlanker Fortschritts-Balken pro Tabellenzeile.
// Schrumpft auf Tablet/Mobile, kein hartes minWidth — sonst sprengt die
// Spalte die Tabelle (siehe Responsive-Cleanup).
function ProgressBar({ prozent }) {
  if (prozent == null) return <span style={{ color: 'var(--text-3)' }}>–</span>;
  const breite = Math.min(prozent, 100);
  const farbe = prozent > 100 ? 'var(--red)' : 'var(--copper)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 60 }}>
      <div style={{
        flex: '1 1 auto', height: 6, background: 'var(--bg-elevated)',
        borderRadius: 999, overflow: 'hidden', minWidth: 24,
      }}>
        <div style={{
          width: `${breite}%`, height: '100%', background: farbe,
          borderRadius: 999, transition: 'width 0.3s',
        }} />
      </div>
      <span className="text-mono" style={{
        fontSize: 12, fontWeight: 600,
        color: prozent > 100 ? 'var(--red)' : 'var(--text-2)',
        textAlign: 'right', whiteSpace: 'nowrap',
      }}>
        {prozent.toFixed(prozent >= 10 ? 0 : 1)} %
      </span>
    </div>
  );
}

// Eine Stat-Karte (Gesamt/Hauptauftrag/Nachträge/Positionen). Wert + optionale
// kleine Sub-Zeile. Layout konsistent zu BaustellenAuswertung.js.
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: 'var(--bg-2)', borderRadius: 12, padding: 16,
      border: '1px solid var(--border)', minWidth: 0,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, color: accent || 'var(--copper)', lineHeight: 1.1, wordBreak: 'break-word' }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// VerwendungenModal + DrilldownButton wurden in
// `components/VerwendungenModal.js` ausgelagert (G.2 Refactor),
// damit die Auswertungs-Page sie wiederverwenden kann.

// A.6.2: Hilfen fuer den Snapshot-Sub-Block in der Bautagebuch-Stunden-Section.
// fmtEuro liefert „–" bei null/NaN, damit fehlende Werte sauber gerendert
// werden statt „€NaN".
function fmtEuro(n) {
  if (n == null) return '–';
  const num = Number(n);
  if (!Number.isFinite(num)) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);
}

// Reihenfolge der Typen + Labels fuer die Typ-Aufteilung in der Stunden-
// Section. Muss mit Backend (computeStundenAggregate) uebereinstimmen.
const TYP_REIHENFOLGE_PF = [
  ['regulaer', 'Regulär'],
  ['regie',    'Regie'],
  ['zusatz',   'Zusatz'],
];

// ── F.2: Bautagebuch-Stunden-Section ───────────────────────────────────────
// Bewusst aus `bautagebuecher` und NICHT aus `zeiterfassung`:
// - Bautagebuch = dokumentierte Leistung (Tagesbericht), Controlling-relevant
// - Zeiterfassung = Anwesenheit/Stempeluhr, kommt später als Abgleich
//
// Heute pauschal pro Tag (kein FK je Monteur, nur Anzahl). Spätere Phase
// bringt eine Tabelle bautagebuch_stunden mit user_id, Rolle, abrechenbar,
// Typ (regulär/regie/zusatz), LV-Bezug — dann auch Pro-Mitarbeiter-Sicht.
//
// Stunden fließen NICHT ins Material-Aggregat oben ein.
function BautagebuchStundenSection({ stunden, navigate }) {
  const liste = stunden?.liste || [];
  const gesamt = parseFloat(stunden?.gesamt) || 0;
  const tage = stunden?.arbeitstage ?? 0;
  const anzahl = stunden?.anzahl_bautagebuecher ?? 0;

  if (liste.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <div className="card-title">Stunden aus Bautagebüchern · 0h</div>
        </div>
        <div style={{
          padding: '14px 0', fontSize: 13, color: 'var(--text-3)',
          textAlign: 'center',
        }}>
          Auf dieser Baustelle gibt es noch keine Bautagebuch-Einträge.
          Sobald Tagesberichte gespeichert werden, erscheinen die Stunden
          hier automatisch.
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-head">
        <div className="card-title">
          Stunden aus Bautagebüchern · {fmtStunden(gesamt)}
          <span style={{ marginLeft: 8, color: 'var(--text-3)', fontWeight: 400, fontSize: 12 }}>
            aus {anzahl} {anzahl === 1 ? 'Bericht' : 'Berichten'}
            {tage > 0 && ` · ${tage} ${tage === 1 ? 'Arbeitstag' : 'Arbeitstage'}`}
          </span>
        </div>
      </div>

      <div style={{
        background: 'var(--bg-elevated)', color: 'var(--text-3)',
        padding: '8px 12px', borderRadius: 'var(--radius)',
        fontSize: 12, marginBottom: 10, lineHeight: 1.5,
      }}>
        Dokumentierte Leistung aus den Tagesberichten. Zeiterfassung
        (Anwesenheit) ist eine separate Ebene und wird später als
        Abgleich/Plausibilitätscheck ergänzt — sie ist <strong>nicht</strong>
        automatisch abrechenbare Leistung.
      </div>

      {/* A.6.2: Detailaggregate aus bautagebuch_stunden (Pro-Person).
          Nur eingeblendet wenn das Backend ein detail-Objekt mitliefert
          UND tatsaechlich Pro-Person-Zeilen mit Stunden > 0 existieren.
          Alle Sub-Zeilen sind individuell conditional - falls eine Sub-
          Dimension leer ist, wird sie nicht angezeigt. Snapshots strikt
          getrennt, kein Aufaddieren, klarer "Keine Rechnung"-Hinweis. */}
      {(() => {
        const detail = stunden?.detail;
        if (!detail) return null;
        const gesamtNum = Number(detail.gesamt?.stunden) || 0;
        if (gesamtNum <= 0) return null;

        const personenGesamt = detail.personen?.gesamt ?? 0;
        const externZeilen = detail.personen?.extern_zeilen ?? 0;
        // A.7 additiv, robust gegen altes Backend ohne die neuen Felder.
        const subEindeutig = detail.personen?.subunternehmer_eindeutig ?? 0;

        const typItems = TYP_REIHENFOLGE_PF
          .map(([k, label]) => ({ key: k, label, sum: Number(detail.by_typ?.[k]) || 0 }))
          .filter(x => x.sum > 0);

        const abrJa = Number(detail.by_abrechenbar?.ja) || 0;
        const abrNein = Number(detail.by_abrechenbar?.nein) || 0;

        const rollen = Array.isArray(detail.by_rolle)
          ? detail.by_rolle.filter(r => (Number(r.stunden) || 0) > 0)
          : [];

        const intern = detail.bewertung?.intern;
        const lv     = detail.bewertung?.lv;
        const internRelevant = intern && (
          (Number(intern.bewertete_stunden) || 0) > 0 ||
          (Number(intern.nicht_bewertbar_stunden) || 0) > 0
        );
        const lvRelevant = lv && (
          (Number(lv.bewertete_stunden) || 0) > 0 ||
          (Number(lv.nicht_bewertbar_stunden) || 0) > 0
        );

        const renderBewertung = (block, label) => {
          const bewertet = Number(block.bewertete_stunden) || 0;
          const nb = Number(block.nicht_bewertbar_stunden) || 0;
          const mig = Number(block.nicht_bewertbar_aus_migration_stunden) || 0;
          return (
            <div style={{ marginTop: 2 }}>
              <span style={{ color: 'var(--text-3)' }}>{label}: </span>
              <span style={{ fontWeight: 500 }}>{fmtEuro(block.wert)}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 11 }}>
                {' '}({fmtStunden(bewertet)} bewertet
                {nb > 0 && (
                  <>
                    , {fmtStunden(nb)} nicht bewertbar
                    {mig > 0 && (
                      <> – davon {fmtStunden(mig)} aus alter Sammelerfassung</>
                    )}
                  </>
                )})
              </span>
            </div>
          );
        };

        return (
          <div style={{
            background: 'var(--bg-elevated)',
            borderRadius: 'var(--radius)',
            padding: '10px 12px',
            marginBottom: 10,
            fontSize: 12,
            lineHeight: 1.55,
          }}>
            <div>
              <span style={{ color: 'var(--text-3)' }}>Personen: </span>
              <span style={{ fontWeight: 600 }}>{personenGesamt}</span>
              {externZeilen > 0 && (
                <span style={{ color: 'var(--text-3)' }}>
                  {' '}(davon {externZeilen}{' '}
                  {externZeilen === 1 ? 'externe Zeile' : 'externe Zeilen'}
                  {subEindeutig > 0 && <> · {subEindeutig} Subunternehmer eindeutig</>})
                </span>
              )}
            </div>

            {typItems.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <span style={{ color: 'var(--text-3)' }}>Typ: </span>
                {typItems.map((x, i) => (
                  <span key={x.key}>
                    {i > 0 && <span style={{ color: 'var(--text-3)' }}> · </span>}
                    {fmtStunden(x.sum)} {x.label}
                  </span>
                ))}
              </div>
            )}

            {(abrJa > 0 || abrNein > 0) && (
              <div style={{ marginTop: 4 }}>
                <span style={{ color: 'var(--text-3)' }}>Abrechenbar: </span>
                {abrJa > 0 && <span>{fmtStunden(abrJa)} ja</span>}
                {abrJa > 0 && abrNein > 0 && (
                  <span style={{ color: 'var(--text-3)' }}> · </span>
                )}
                {abrNein > 0 && <span>{fmtStunden(abrNein)} nein</span>}
              </div>
            )}

            {rollen.length > 1 && (
              <div style={{ marginTop: 4 }}>
                <span style={{ color: 'var(--text-3)' }}>Funktion: </span>
                {rollen.map((r, i) => (
                  <span key={r.rolle_id ?? `r-${i}`}>
                    {i > 0 && <span style={{ color: 'var(--text-3)' }}> · </span>}
                    {fmtStunden(r.stunden)} {r.rolle_bezeichnung || '–'}
                  </span>
                ))}
              </div>
            )}

            {(internRelevant || lvRelevant) && (
              <div style={{
                marginTop: 8, paddingTop: 8,
                borderTop: '1px solid var(--border)',
              }}>
                {internRelevant && renderBewertung(intern, 'Interne Kosten (Snapshot)')}
                {lvRelevant && renderBewertung(lv, 'Leistungsnachweis (Snapshot)')}
                <div style={{
                  marginTop: 6, fontSize: 11,
                  color: 'var(--text-3)', fontStyle: 'italic',
                }}>
                  Aus Stundensätzen zum Buchungszeitpunkt. Keine Rechnung.
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="table-wrap table-mobile-stack">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Bautagebuch</th>
              <th>Std/Tag</th>
              <th>Monteure</th>
              <th>Mannstunden</th>
              <th>Bauleiter</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {liste.map(b => {
              const statusBadge = b.status === 'fertig'
                ? <span className="badge badge-green">Fertig</span>
                : <span className="badge badge-grey">Entwurf</span>;
              return (
                <tr key={b.id}>
                  <td data-label="Datum" className="text-mono" style={{ whiteSpace: 'nowrap' }}>
                    {fmtDatum(b.datum)}
                  </td>
                  <td data-label="" className="td-title" style={{ fontWeight: 500 }}>
                    #{b.id}
                  </td>
                  <td data-label="Std/Tag" className="text-mono">
                    {b.std_gesamt != null ? fmtNum(b.std_gesamt) + ' h' : '–'}
                  </td>
                  <td data-label="Monteure" className="text-mono" style={{ textAlign: 'center' }}>
                    {b.anzahl_monteure ?? '–'}
                  </td>
                  <td data-label="Mannstunden" className="text-mono" style={{ fontWeight: 600, color: 'var(--copper)' }}>
                    {fmtStunden(b.gesamtstunden)}
                  </td>
                  <td data-label="Bauleiter">{b.bauleiter || '–'}</td>
                  <td data-label="Status">{statusBadge}</td>
                  <td data-label="" className="td-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/bautagebuch/${b.id}`)}
                      title="Bautagebuch öffnen"
                    >→ Öffnen</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Tabelle für „Nicht zugeordnete Buchungen" (F.1).
// Zeigt material_einlagen ohne projekt_position_id, aggregiert nach
// Bezeichnung+Einheit. Bewusst KEIN Fortschritts-Balken und KEIN Wert —
// es gibt kein Soll, das wäre irreführend. Nur Ist-Menge und Anzahl der
// Bautagebücher als „kommt das oft vor — sollte vielleicht eine echte
// Position werden?"-Indiz.
function NichtZugeordnetTabelle({ items, onDrilldown }) {
  if (!items || items.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <div className="card-title">Nicht zugeordnete Buchungen · 0</div>
        </div>
        <div style={{
          padding: '14px 0', fontSize: 13, color: 'var(--text-3)',
          textAlign: 'center',
        }}>
          Alle Material-/Leistungsbuchungen dieser Baustelle sind einer
          LV-Position zugeordnet. ✓
        </div>
      </div>
    );
  }
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-head">
        <div className="card-title">Nicht zugeordnete Buchungen · {items.length}</div>
      </div>
      <div style={{
        background: 'var(--amber-bg)', color: 'var(--amber)',
        padding: '8px 12px', borderRadius: 'var(--radius)',
        fontSize: 12, marginBottom: 10, lineHeight: 1.5,
      }}>
        Buchungen aus Bautagebüchern ohne LV-Position-Zuordnung.
        Fließen <strong>nicht</strong> in den Soll-/Ist-Fortschritt oben ein.
        Häufig wiederkehrende Einträge sollten als Projektposition angelegt werden.
      </div>
      <div className="table-wrap table-mobile-stack">
        <table>
          <thead>
            <tr>
              <th>Pos.-Nr.</th>
              <th>Bezeichnung</th>
              <th>Einheit</th>
              <th>Menge gesamt</th>
              <th>Verwendungen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m, i) => (
              <tr key={i}>
                <td data-label="Pos.-Nr." className="text-mono text-copper" style={{ fontWeight: 600 }}>
                  {m.pos_nr || '–'}
                </td>
                <td data-label="" className="td-title" style={{ fontWeight: 500 }}>
                  {m.bezeichnung || '–'}
                </td>
                <td data-label="Einheit" style={{ textAlign: 'center' }}>
                  {m.einheit || '–'}
                </td>
                <td data-label="Menge gesamt" className="text-mono" style={{ fontWeight: 600 }}>
                  {fmtNum(m.menge_gesamt)}
                </td>
                <td data-label="Verwendungen" className="text-mono" style={{ color: 'var(--text-3)' }}>
                  {m.verwendungen} {m.verwendungen === 1 ? 'Bautagebuch' : 'Bautagebücher'}
                </td>
                <td data-label="" className="td-actions">
                  <DrilldownButton
                    onClick={() => onDrilldown(m)}
                    title="Buchungen ansehen"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Eine gruppierte Positions-Tabelle (Hauptauftrag oder Nachträge).
// Mobile-Stacking via .table-mobile-stack + data-label.
function PositionsTabelle({ titel, positionen, onDrilldown }) {
  if (positionen.length === 0) return null;
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-head">
        <div className="card-title">{titel} · {positionen.length}</div>
      </div>
      <div className="table-wrap table-mobile-stack">
        <table>
          <thead>
            <tr>
              <th>Pos.-Nr.</th>
              <th>Bezeichnung</th>
              <th>Einheit</th>
              <th>Soll</th>
              <th>Ist</th>
              <th>Rest</th>
              <th>Fortschritt</th>
              <th>Wert (Soll)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {positionen.map(p => {
              const sollWert = (parseFloat(p.sollmenge) || 0) * (parseFloat(p.einheitspreis) || 0);
              return (
                <tr key={p.id}>
                  <td data-label="Pos.-Nr." className="text-mono text-copper" style={{ fontWeight: 600 }}>{p.pos_nr || '–'}</td>
                  <td data-label="" className="td-title" style={{ fontWeight: 500 }}>{p.bezeichnung}</td>
                  <td data-label="Einheit" style={{ textAlign: 'center' }}>{p.einheit || '–'}</td>
                  <td data-label="Soll" className="text-mono">{fmtNum(p.sollmenge)}</td>
                  <td data-label="Ist" className="text-mono">{fmtNum(p.verbraucht_menge)}</td>
                  <td data-label="Rest" className="text-mono" style={{ color: p.rest === 0 ? 'var(--green)' : 'var(--text-2)' }}>
                    {p.rest != null ? fmtNum(p.rest) : '–'}
                  </td>
                  <td data-label="Fortschritt"><ProgressBar prozent={p.prozent} /></td>
                  <td data-label="Wert (Soll)" className="text-mono" style={{ color: 'var(--text-3)' }}>
                    {sollWert > 0 ? fmtEur(sollWert) : '–'}
                  </td>
                  <td data-label="" className="td-actions">
                    <DrilldownButton
                      onClick={() => onDrilldown(p)}
                      title="Verwendungen ansehen"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Projektfortschritt() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();

  const [baustellen, setBaustellen] = useState([]);
  const [baustelleId, setBaustelleId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // Drilldown-Modal: null = zu, sonst { kind, position?, item?, baustelleId }
  const [drilldown, setDrilldown] = useState(null);

  // Stabile Handler — verhindern dass die Tabellen-Komponenten unnötig
  // re-rendern und schließen das Modal sauber.
  const openPositionDrill = useCallback((position) => {
    setDrilldown({ kind: 'position', position });
  }, []);
  const openFreiDrill = useCallback((item) => {
    setDrilldown({ kind: 'frei', item, baustelleId });
  }, [baustelleId]);
  const closeDrill = useCallback(() => setDrilldown(null), []);

  // Wenn Baustelle wechselt, evtl. offenes Modal schließen — sonst verweist
  // es auf eine Position der vorherigen Baustelle.
  useEffect(() => { setDrilldown(null); }, [baustelleId]);

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/'); return; }
    api.get('/baustellen').then(r => setBaustellen(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user, navigate]);

  useEffect(() => {
    if (!baustelleId) { setData(null); setErr(''); return; }
    setLoading(true);
    setErr('');
    api.get(`/projektfortschritt/${baustelleId}`)
      .then(r => setData(r.data))
      .catch(() => { setData(null); setErr('Konnte Projektfortschritt nicht laden.'); })
      .finally(() => setLoading(false));
  }, [baustelleId]);

  if (user?.role !== 'admin') return null;

  const summary = data?.summary;

  // Sub-Zeile für die Aggregat-Stat-Cards: zeigt €-Wert wenn wert-gewichtet,
  // sonst Hinweis auf Mengen-Modus, sonst nichts.
  const summarySub = (s) => {
    if (!s || s.prozent == null) return null;
    if (s.modus === 'wert') {
      return `${fmtEur(s.ist_wert)} / ${fmtEur(s.soll_wert)}`;
    }
    return 'mengen-gewichtet (keine Preise)';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Projektfortschritt</div>
          <div className="page-sub">Soll-Ist je Baustelle über die gesamte Laufzeit (kein Zeitraum-Filter)</div>
        </div>
      </div>

      {/* Filter — bewusst nur Baustelle, kein Zeitraum (das ist Auswertung) */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="form-grid form-grid-2">
          <div className="form-group">
            <label className="form-label">Baustelle</label>
            <select
              className="form-select"
              value={baustelleId}
              onChange={e => setBaustelleId(e.target.value)}
            >
              <option value="">— Baustelle wählen —</option>
              {baustellen.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {!baustelleId && (
        <div className="card">
          <div className="empty">
            <div className="empty-icon">📈</div>
            <h3>Keine Baustelle gewählt</h3>
            <p>Oben eine Baustelle wählen, um den aktuellen Projektfortschritt zu sehen.</p>
          </div>
        </div>
      )}

      {baustelleId && loading && (
        <div className="card">
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            Wird berechnet…
          </div>
        </div>
      )}

      {err && (
        <div className="alert alert-error">{err}</div>
      )}

      {data && !loading && (
        <>
          {/* Baustelle-Header */}
          <div className="card card-accent" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4, wordBreak: 'break-word' }}>
                  {data.baustelle.name}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-3)', wordBreak: 'break-word' }}>
                  {[data.baustelle.adresse, data.baustelle.plz, data.baustelle.ort].filter(Boolean).join(' ')}
                  {data.baustelle.auftraggeber && ` · AG: ${data.baustelle.auftraggeber}`}
                </div>
              </div>
            </div>
          </div>

          {/* Stat-Cards — auto-fit Grid, stacken auf schmalen Geräten */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
            marginBottom: 14,
          }}>
            <StatCard
              label="Gesamt"
              value={fmtProz(summary.gesamt.prozent)}
              sub={summarySub(summary.gesamt)}
            />
            <StatCard
              label="Hauptauftrag"
              value={fmtProz(summary.hauptauftrag.prozent)}
              sub={summarySub(summary.hauptauftrag)}
            />
            <StatCard
              label="Nachträge"
              value={fmtProz(summary.nachtraege.prozent)}
              sub={summarySub(summary.nachtraege)}
              accent={summary.nachtraege.anz_positionen > 0 ? 'var(--amber)' : 'var(--text-3)'}
            />
            <StatCard
              label="Positionen"
              value={summary.gesamt.anz_positionen}
              sub={`${summary.hauptauftrag.anz_positionen} Haupt · ${summary.nachtraege.anz_positionen} Nachtrag`}
              accent="var(--text-1)"
            />
            {/* F.2: Stunden-Stat-Card aus Bautagebuch (dokumentierte
                Leistung). Bewusst getrennt vom Material-Aggregat —
                fließt NICHT in summary.gesamt ein. Zeiterfassung
                (Anwesenheit) folgt später als Abgleich-Ebene. */}
            <StatCard
              label="Stunden (BT)"
              value={fmtStunden(data.stunden?.bautagebuch?.gesamt)}
              sub={data.stunden?.bautagebuch?.anzahl_bautagebuecher > 0
                ? `${data.stunden.bautagebuch.anzahl_bautagebuecher} ${data.stunden.bautagebuch.anzahl_bautagebuecher === 1 ? 'Bericht' : 'Berichte'} · ${data.stunden.bautagebuch.arbeitstage} ${data.stunden.bautagebuch.arbeitstage === 1 ? 'Tag' : 'Tage'}`
                : null}
              accent={(parseFloat(data.stunden?.bautagebuch?.gesamt) || 0) > 0 ? 'var(--copper)' : 'var(--text-3)'}
            />
          </div>

          {/* Wenn keine Positionen vorhanden */}
          {data.positionen.length === 0 && (
            <div className="card">
              <div className="empty">
                <div className="empty-icon">📋</div>
                <h3>Keine aktiven Positionen</h3>
                <p>Für diese Baustelle sind noch keine Projektpositionen gepflegt.</p>
              </div>
            </div>
          )}

          {/* Gruppierte Tabellen */}
          {data.positionen.length > 0 && (
            <>
              <PositionsTabelle
                titel="Hauptauftrag"
                positionen={data.positionen.filter(p => !p.nachtrag)}
                onDrilldown={openPositionDrill}
              />
              <PositionsTabelle
                titel="Nachträge"
                positionen={data.positionen.filter(p => p.nachtrag)}
                onDrilldown={openPositionDrill}
              />
            </>
          )}

          {/* F.2: Bautagebuch-Stunden — eigener Block, fließt NICHT ins
              Material-Aggregat ein. Erste Brücke zwischen Material und
              Aufwand. Zeiterfassung kommt später separat als Abgleich. */}
          <BautagebuchStundenSection
            stunden={data.stunden?.bautagebuch}
            navigate={navigate}
          />

          {/* F.1: Nicht zugeordnete Buchungen — fließen NICHT in Stat-Cards
              oben ein, werden aber transparent angezeigt damit nichts
              verschwindet. */}
          <NichtZugeordnetTabelle
            items={data.nicht_zugeordnet}
            onDrilldown={openFreiDrill}
          />
        </>
      )}

      {/* F.1.1: Drilldown-Modal (lazy) — gemountet außerhalb der Datenliste,
          damit es offen bleibt während `data` neu lädt. */}
      <VerwendungenModal trigger={drilldown} onClose={closeDrill} />
    </div>
  );
}
